import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { usePageTitle } from '../hooks/usePageTitle';
import { useProjects } from '../hooks/useProjects';
import { useMe } from '../hooks/useUsers';
import {
  getLatestUpdate,
  triggerRefresh,
  type ProjectTile,
  type LastUpdate,
  type LatestUpdateResponse,
} from '../api/projects';
import type { Permission } from '../api/users';
import { ApiClientError } from '../api/client';

const ADMIN_PERMISSION: Permission = 'VIEW_PROJECT_DETAILS';

/**
 * Portal landing — lists every sub-project the current user can see. Wiki and static tiles route through the
 * SPA shell so the portal nav stays visible; proxy tiles use a full-page navigation (`<a href>`) to bypass
 * the SPA, since proxied targets serve their own UI directly.
 *
 * Admins see a status line per tile (red on error) plus info + refresh buttons.
 */
export default function LandingPage() {
  usePageTitle('Home');
  const { data, error, isLoading } = useProjects();
  const { data: me } = useMe();
  const isAdmin = me?.permissions?.includes(ADMIN_PERMISSION) ?? false;

  if (isLoading) return <p className="muted">Loading...</p>;
  if (error instanceof ApiClientError && error.status === 403) {
    return <p className="muted">No projects available for your account.</p>;
  }
  if (error) return <p style={{ color: 'var(--danger)' }}>Error: {error.message}</p>;
  if (!data || data.length === 0) {
    return <p className="muted">No projects configured.</p>;
  }
  return (
    <div className="landing" data-testid="landing-page">
      <h1>Projects</h1>
      <ul className="landing__tiles" data-testid="landing-tiles">
        {data.map((p) => (
          <li key={p.slug} data-testid={`landing-tile-${p.slug}`}>
            <Tile project={p} isAdmin={isAdmin} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Tile({ project, isAdmin }: { project: ProjectTile; isAdmin: boolean }) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <div className="tile">
      <TileLink project={project} />
      {(project.lastUpdate || isAdmin) && (
        <div className="tile__footer">
          {project.lastUpdate ? (
            <LastUpdateLine update={project.lastUpdate} slug={project.slug} />
          ) : (
            <span className="muted tile__status" data-testid={`tile-last-update-${project.slug}`}>
              Never updated
            </span>
          )}
          {isAdmin && (
            <div className="tile__actions">
              <button
                type="button"
                className="tile__icon"
                aria-label={`Update details for ${project.name}`}
                data-testid={`tile-details-${project.slug}`}
                onClick={() => setDetailsOpen(true)}
              >
                i
              </button>
              <RefreshButton projectId={project.id} slug={project.slug} />
            </div>
          )}
        </div>
      )}
      {isAdmin && (
        <UpdateDetailsDialog project={project} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
      )}
    </div>
  );
}

function TileLink({ project }: { project: ProjectTile }) {
  const dest = destinationFor(project);
  const body = (
    <>
      <strong>{project.name}</strong>
      <p className="muted">{project.description}</p>
      <span className="muted">type: {project.type}</span>
    </>
  );
  if (project.type === 'proxy') {
    return (
      <a href={dest} className="tile__link">
        {body}
      </a>
    );
  }
  return (
    <Link to={dest} className="tile__link">
      {body}
    </Link>
  );
}

function LastUpdateLine({ update, slug }: { update: LastUpdate; slug: string }) {
  const formatted = new Date(update.ts).toLocaleString();
  const cls = update.status === 'error' ? 'tile__status tile__status--error' : 'tile__status';
  return (
    <span className={cls} data-testid={`tile-last-update-${slug}`}>
      Last updated: {formatted}
    </span>
  );
}

function RefreshButton({ projectId, slug }: { projectId: string; slug: string }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => triggerRefresh(projectId),
    onSuccess: () => {
      // The server runs the refresh in the background; invalidate so the UI refetches once it
      // (eventually) writes a new UpdateEntry. The user can also reopen the details modal for the
      // freshest state.
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project-latest-update', projectId] });
    },
  });

  return (
    <button
      type="button"
      className="tile__icon"
      aria-label={`Refresh ${projectId} now`}
      data-testid={`tile-refresh-${slug}`}
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {mutation.isPending ? '...' : '↻'}
    </button>
  );
}

function UpdateDetailsDialog({
  project,
  open,
  onClose,
}: {
  project: ProjectTile;
  open: boolean;
  onClose: () => void;
}) {
  const { data, error, isLoading } = useQuery<LatestUpdateResponse>({
    queryKey: ['project-latest-update', project.id],
    queryFn: () => getLatestUpdate(project.id),
    enabled: open,
    retry: false,
  });
  const is404 = error instanceof ApiClientError && error.status === 404;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        data-testid={`tile-modal-${project.slug}`}
      >
        {project.name} — last update
        <IconButton onClick={onClose} size="small" aria-label="Close" data-testid="modal-close-button">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <p className="muted">Loading...</p>}
        {is404 && <p className="muted">No update entries yet for this project.</p>}
        {error && !is404 && <p style={{ color: 'var(--danger)' }}>Error: {error.message}</p>}
        {data && (
          <dl className="update-details">
            <dt>Status</dt>
            <dd>{data.entry.status}</dd>
            <dt>Phase</dt>
            <dd>{data.entry.phase}</dd>
            <dt>When</dt>
            <dd>{new Date(data.entry.ts).toLocaleString()}</dd>
            <dt>Summary</dt>
            <dd>{data.entry.summary}</dd>
            {data.entry.counts && Object.keys(data.entry.counts).length > 0 && (
              <>
                <dt>Counts</dt>
                <dd>
                  <ul>
                    {Object.entries(data.entry.counts).map(([k, v]) => (
                      <li key={k}>
                        <code>{k}</code>: {v}
                      </li>
                    ))}
                  </ul>
                </dd>
              </>
            )}
            {data.entry.error && (
              <>
                <dt>Output</dt>
                <dd>
                  <pre className="update-details__output">{data.entry.error}</pre>
                </dd>
              </>
            )}
          </dl>
        )}
      </DialogContent>
    </Dialog>
  );
}

function destinationFor(p: ProjectTile): string {
  if (p.type === 'wiki') return `/${p.slug}/wiki/index`;
  if (p.type === 'static') return `/${p.slug}`;
  return `/api/v1/${p.slug}/`;
}
